import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { RemoteConfigAppResponse, RemoteConfigBanners } from '@vi-iot/client';

import {
  AppState,
  AuthSelectors,
  AuthState,
  BookingSubscriptionPackageOptions,
  COUNTRIES_ERROR_CODE_SEARCH,
  InstallationsAnnouncementStorageValues,
  RemoteConfigState,
} from '@store';

import { environment } from '../../../environments/environment';
import { FlutterHeatingSystemsHelper } from 'src/app/+flutter-heating-systems/flutter-heating-systems/flutter-heating-systems.helper';

export namespace RemoteConfigSelectors {
  export const remoteConfig = (state: AppState): RemoteConfigState => state.remoteConfig;
  export const auth = (state: AppState): AuthState => state.auth;

  export function getState(): MemoizedSelector<
    AppState,
    RemoteConfigState,
    DefaultProjectorFn<RemoteConfigState>
  > {
    return createSelector(remoteConfig, (state: RemoteConfigState) => state);
  }

  export function getRemoteConfig(): MemoizedSelector<
    AppState,
    RemoteConfigAppResponse,
    DefaultProjectorFn<RemoteConfigAppResponse>
  > {
    return createSelector(remoteConfig, (state: RemoteConfigState) => state.remoteConfig);
  }

  export function isLearningRoomBannerAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.isLearningRoomBannerAvailable
    );
  }

  export function isE3HeatPumpMarketLaunchBannerVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.isE3HeatPumpMarketLaunchBannerVisible
    );
  }

  export function isBannerVisible(
    banner: RemoteConfigBanners
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(remoteConfig, (state: RemoteConfigState) => state.remoteConfig[banner]);
  }

  export function isNewVitoguideFeaturesBannerAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.isNewVitoguideFeaturesBannerAvailable
    );
  }

  export function isPaidFeaturesBannerAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.isPaidFeaturesBannerAvailable
    );
  }

  export function hasAccessToProPlusFeaturesByVkOrg(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.areProPlusFeaturesForVkOrgAvailable
    );
  }

  export function hasAccessToProPlusFeaturesByCompanyId(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.areProPlusFeaturesForCompanyIdAvailable
    );
  }

  export function selectTypeOfBookingPackage(): MemoizedSelector<
    AppState,
    BookingSubscriptionPackageOptions,
    DefaultProjectorFn<BookingSubscriptionPackageOptions>
  > {
    return createSelector(
      remoteConfig,
      AuthSelectors.isCompanyFromUsaOrCanada(),
      AuthSelectors.isCommercialUser,
      (state: RemoteConfigState, isCompanyFromUsaOrCanada: boolean, isCommercialUser: boolean) => {
        if (isCommercialUser) {
          return BookingSubscriptionPackageOptions.Commercial;
        } else if (isCompanyFromUsaOrCanada) {
          return BookingSubscriptionPackageOptions.UserUsaCanada;
        } else if (state.remoteConfig?.flatRateVkorg) {
          return BookingSubscriptionPackageOptions.FlatRate;
        } else {
          return BookingSubscriptionPackageOptions.Normal;
        }
      }
    );
  }

  export function hasAccessToWholeInstallationsTable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      AuthSelectors.isRsaUser(),
      (remoteConfigState: RemoteConfigState, isRsaUser: boolean | null) => {
        const hasAccessToProFeatures =
          remoteConfigState.remoteConfig.areProPlusFeaturesForCompanyIdAvailable ||
          remoteConfigState.remoteConfig.areProPlusFeaturesForVkOrgAvailable;

        return !isRsaUser && hasAccessToProFeatures;
      }
    );
  }

  export function isBookingProButtonEnabled(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.bookingProButtonEnabled
    );
  }

  export function isBookingPlusButtonEnabled(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) => state.remoteConfig.bookingPlusButtonEnabled
    );
  }

  export const isFlatRate = createSelector(
    remoteConfig,
    (remoteConfigState): boolean => remoteConfigState.remoteConfig?.flatRateVkorg
  );

  export const isFirmwareUpdateAvailable = createSelector(
    remoteConfig,
    (remoteConfigState): boolean => remoteConfigState.remoteConfig?.isFirmwareUpdateAvailable
  );

  export const appInsightsUserLogLevel = createSelector(
    remoteConfig,
    (remoteConfigState): number => remoteConfigState.remoteConfig?.appInsightsUserLogLevel
  );

  export function showInstallationsAnnouncementBanner(
    storage: InstallationsAnnouncementStorageValues
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteConfig,
      (state): boolean =>
        state.remoteConfig.isViguideRenameBannerAvailable &&
        storage.hidePermanently !== 'true' &&
        storage.temporarilyHideForToken !== storage.token
    );
  }

  export const isHardwareBundlingAvailable = createSelector(
    remoteConfig,
    (state: RemoteConfigState) => state.remoteConfig.isHardwareBundlingAvailable
  );

  export const azureCertificateFirmwareCheck = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.azureCertificateFirmwareCheck
  );

  export const isVicarePlansTileAvailable = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.isVicarePlansTileAvailable
  );

  export const isEcotronicAvailable = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.isEcotronicAvailable
  );

  export function shouldFetchEnergyConsumptionDataForLegacyHeatpump(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (state: RemoteConfigState) =>
        state.remoteConfig.shouldFetchEnergyConsumptionDataForLegacyHeatpump
    );
  }

  export const isSmartHomeComponentsAvailable = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.isSmartHomeComponentsAvailable
  );

  export const isSmartHomeComponentsUsingMock = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.isSmartHomeComponentsUsingMock
  );

  export const selectIsAppInsightsLoggingDataAboutDhw = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.isAppInsightsLoggingDataAboutDhw
  );

  export const selectCountriesWithSmartHomeComponent = createSelector(
    remoteConfig,
    (state): string[] => {
      let availableCountries = [];
      if (typeof state.remoteConfig.countriesWithSmartHomeComponent === 'string') {
        availableCountries = state.remoteConfig.countriesWithSmartHomeComponent
          .split(';')
          .map((country) => country.toLowerCase());
      }

      if (!environment.production) {
        const invalidKeys = availableCountries.filter(
          (country) =>
            !COUNTRIES_ERROR_CODE_SEARCH.some(
              (code) => code.toLowerCase() === country.toLowerCase()
            )
        );
        if (invalidKeys.length > 0) {
          console.error(
            `countriesWithSmartHomeComponent contains invalid value: ${invalidKeys.join(',')}`
          );
        }
      }

      return availableCountries;
    }
  );

  export const isApplicationBetaVersionAvailable = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.isApplicationBetaVersionAvailable
  );

  export const isRestrictedAccessForTestAutomationInstallationsEnabled = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.restrictedAccessForTestAutomationInstallationsEnabled
  );

  export const selectLoaded = createSelector(remoteConfig, (state): boolean => state.loaded);

  export const isBoilerSerialEditable = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.isBoilerSerialEditable
  );

  export const selectBlockEndCustomers = createSelector(
    remoteConfig,
    (state): boolean => state.remoteConfig.blockEndCustomers
  );

  export const selectServiceTicketVkorgList = createSelector(
    remoteConfig,
    (state: RemoteConfigState): string[] =>
      _stringToArrayConverter(state.remoteConfig.serviceTicketVkorgList)
  );

  export const selectServiceTicketCallNowBlacklist = createSelector(
    remoteConfig,
    (state: RemoteConfigState): string[] =>
      _stringToArrayConverter(state.remoteConfig.serviceTicketCallNowBlacklist)
  );

  const _stringToArrayConverter = (input: string): Array<string> => {
    return (
      input
        ?.split(',')
        ?.map((entry) => entry.trim())
        ?.filter((entry) => entry.length > 0) ?? []
    );
  };

  export const selectServiceTicketAllowedByCompanyId = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.serviceTicketAllowedByCompanyId
  );

  export const selectShowGenericInfoBanner = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.showGenericInfoBanner
  );

  export const selectXOTAFirmwareVersionBlackList = createSelector(
    remoteConfig,
    (state: RemoteConfigState): string[] | undefined =>
      state.remoteConfig.xOTAFirmwareVersionBlackList
        ?.split(',')
        .map((el) => el.trim())
        .filter((entry) => entry.length > 0) || undefined
  );

  export const selectIsSurveyBannerVisible = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.isSurveyBannerVisible
  );

  export const selectIsDomainSwitchInformationBannerVisible = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.domainSwitchInformationBannerVisible
  );

  export const selectPowerLimitationsEventLogVisible = createSelector(
    remoteConfig,
    (state: RemoteConfigState): string[] =>
      _stringToArrayConverter(state.remoteConfig?.powerLimitationsEventLogVisible)
  );

  export const selectRsaViUnifyAccessIds = createSelector(
    remoteConfig,
    (state: RemoteConfigState): string[] =>
      _stringToArrayConverter(state.remoteConfig.rsaViUnifyAccessIds)
  );

  export function selectHeatingSystemsAccess(): MemoizedSelector<
    AppState,
    FlutterHeatingSystemsHelper.HeatingSystemsAccess,
    DefaultProjectorFn<FlutterHeatingSystemsHelper.HeatingSystemsAccess>
  > {
    return createSelector(
      remoteConfig,
      AuthSelectors.selectPrimaryVkorg,
      (state, vkorg): FlutterHeatingSystemsHelper.HeatingSystemsAccess =>
        state.remoteConfig?.heatingSystemsAccess
          ? JSON.parse(state.remoteConfig.heatingSystemsAccess)?.[vkorg]
          : undefined
    );
  }

  export const selectIsHeatingSystemsLinkVisibleInSideMenu = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.isHeatingSystemsLinkVisibleInSideMenu
  );

  export const selectIsWhatsAppBannerVisible = createSelector(
    remoteConfig,
    (state: RemoteConfigState): boolean => state.remoteConfig.isWhatsAppBannerVisible
  );
}
